// What a stack's rail shows of each member's state: the node's fill (its
// review state), its ring (its checks), the stretch of line into it, and
// the header's stack map. Pure: the dashboard renders what comes back.
import type { RailRow, RepoNumber } from './stackRail';
import type { StackRow } from './stacks';
import type { Turn } from './stackRail';
import { reviewNotes, reviewOutcome, type ReviewSummary } from './reviewSummary';

/** A member's review state, the node's fill. */
export type MemberState =
  /** Waiting on the viewer. */
  | 'yours'
  | 'approved'
  /** Changes requested. */
  | 'changes'
  /** Waiting on its author only. */
  | 'author'
  /** Open and waiting on others, or on no one known. */
  | 'open'
  | 'merged'
  | 'closed'
  /** Not in this section, and not waiting on the viewer: hollow. */
  | 'context'
  /** Not loaded: hollow and dashed. */
  | 'missing';

/** A member's checks, the node's ring. */
export type CheckState = 'passing' | 'pending' | 'failing' | 'none';

/** GitHub's `statusCheckRollup.state`. */
export function checkState(rollup: string | null | undefined): CheckState {
  if (!rollup) return 'none';
  if (rollup === 'SUCCESS') return 'passing';
  if (rollup === 'FAILURE' || rollup === 'ERROR') return 'failing';
  return 'pending';
}

/**
 * A loaded member's review state. Merged and closed come first; then the
 * viewer's turn (whatever the reviews say); a context row not waiting on
 * the viewer is hollow; then the reviewers' outcome (`reviewOutcome`:
 * changes requested, or approved), and waiting on the author.
 */
export function memberState(m: {
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  review: 'approved' | 'changes' | null;
  turn: Turn['kind'];
  context: boolean;
}): MemberState {
  if (m.state === 'MERGED') return 'merged';
  if (m.state === 'CLOSED') return 'closed';
  if (m.turn === 'you') return 'yours';
  if (m.context) return 'context';
  if (m.review === 'changes') return 'changes';
  if (m.review === 'approved') return 'approved';
  if (m.turn === 'author') return 'author';
  return 'open';
}

const TEXT: Record<MemberState, string> = {
  yours: 'your turn',
  approved: 'approved',
  changes: 'changes requested',
  author: 'waiting on author',
  open: 'in review',
  merged: 'merged',
  closed: 'closed',
  context: 'not in this section',
  missing: 'not loaded',
};

/** A member state in words. */
export const stateText = (s: MemberState) => TEXT[s];

/** A stretch of the rail's line into a pull request. */
export type SegState = 'done' | 'yours' | 'missing' | 'muted';

/** Solid when the pull request is approved or merged, the viewer's when it waits on them, dashed when not loaded, muted otherwise. */
export function segState(s: MemberState): SegState {
  if (s === 'approved' || s === 'merged') return 'done';
  if (s === 'yours') return 'yours';
  if (s === 'missing') return 'missing';
  return 'muted';
}

/** One line through a row: the stretch above its node and the one below, each null where none is drawn. */
export interface LinePart {
  above: SegState | null;
  below: SegState | null;
}

export interface RowLines {
  /** Lane 0's line through the row, null where the line does not reach it. */
  trunk: LinePart | null;
  /** A branch's line (lane 1) through the row. */
  branch: LinePart | null;
  /** The curve off the rail into a branch's first row. */
  elbow: SegState | null;
}

const part = (above: SegState | null, below: SegState | null): LinePart | null => (above || below ? { above, below } : null);

/**
 * The lines through each row, every stretch in the state of the pull
 * request it leads into: the next row on its lane. Lane 0 runs from the
 * first row to its last row, past any branch; a branch's line runs from its
 * first row (reached by the elbow) to its last before lane 0 or the next
 * branch. `stateOf` is each row's member state (a gathered row's, its first
 * member's).
 */
export function railLines<R extends StackRow>(rows: RailRow<R>[], stateOf: (r: RailRow<R>) => MemberState): RowLines[] {
  const seg = (k: number) => segState(stateOf(rows[k]));
  const nextOn0 = (k: number) => {
    for (let j = k + 1; j < rows.length; j++) if (rows[j].lane === 0) return j;
    return -1;
  };
  return rows.map((r, k) => {
    const n0 = nextOn0(k);
    const below0 = n0 < 0 ? null : seg(n0);
    if (r.lane === 0) return { trunk: part(k === 0 ? null : seg(k), below0), branch: null, elbow: null };
    const next = rows[k + 1];
    const continues = !!next && next.lane === 1 && !next.elbow;
    return {
      trunk: part(below0, below0),
      branch: part(r.elbow ? null : seg(k), continues ? seg(k + 1) : null),
      elbow: r.elbow ? seg(k) : null,
    };
  });
}

/** One member in the header's stack map. */
export interface MapCell {
  /** Its stack position. */
  index: number;
  /** The rail row that shows it. */
  row: number;
  state: MemberState;
  /** Its number, when known. */
  pr: RepoNumber | null;
}

/**
 * One cell per stack position, in rail order. A row of the section (or a
 * context row waiting on the viewer) is in `stateOf`'s state; a gathered
 * row is split into its members, a context row hollow and each missing
 * position not loaded (numbered where GitHub named it).
 */
export function mapCells<R extends StackRow>(rows: RailRow<R>[], stateOf: (r: Extract<RailRow<R>, { kind: 'pr' }>) => MemberState): MapCell[] {
  const cells: MapCell[] = [];
  rows.forEach((r, k) => {
    if (r.kind === 'pr') {
      cells.push({ index: r.index, row: k, state: stateOf(r), pr: { repo: r.node.row.pr.repository.nameWithOwner, number: r.node.row.pr.number } });
      return;
    }
    for (const m of r.members) {
      if (m.kind === 'pr') {
        cells.push({ index: m.index, row: k, state: 'context', pr: { repo: m.row.pr.repository.nameWithOwner, number: m.row.pr.number } });
        continue;
      }
      const span = m.to - m.from;
      for (let i = 0; i <= span; i++) {
        const number = i === 0 ? m.first : i === span ? m.last : null;
        cells.push({ index: m.index + i, row: k, state: 'missing', pr: number === null ? null : { repo: m.repo, number } });
      }
    }
  });
  return cells;
}

/** A row's review state, for every mark that shows it (see `rowReview`). */
export interface RowReview {
  /** The node's fill, its line and its map cell. */
  member: MemberState;
  /** The row's review pill. */
  pill: 'approved' | 'changes' | null;
  /** Beside the avatars: older reviews not read, a state from them, approvals short of what GitHub requires. */
  notes: string[];
}

/**
 * A row's review state for every mark that shows it: the node's fill, its
 * line and map cell (`member`), the row's review pill, and the notes beside
 * its avatars. All come from one `reviewOutcome`, so they never disagree.
 */
export function rowReview(m: {
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  review: ReviewSummary | null;
  decision: string | null;
  turn: Turn['kind'];
  context: boolean;
}): RowReview {
  const o = reviewOutcome(m.review, m.decision);
  return {
    member: memberState({ state: m.state, review: o.state, turn: m.turn, context: m.context }),
    pill: o.state,
    notes: m.review ? reviewNotes(m.review, m.decision, o) : [],
  };
}
